import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Activity,
  Zap,
  TrendingUp
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

interface Stats {
  total_jobs: number;
  enabled_jobs: number;
  disabled_jobs: number;
  total_executions_24h: number;
  successful_executions_24h: number;
  failed_executions_24h: number;
  avg_execution_time_ms: number;
  next_scheduled_jobs: Array<{
    job_id: string;
    name: string;
    next_run: string;
  }>;
  recent_failures: Array<{
    id: string;
    job_name: string;
    error_message: string;
    started_at: string;
  }>;
  executions_by_hour: Array<{
    hour: string;
    successful: number;
    failed: number;
  }>;
  jobs_by_status: Array<{
    status: string;
    count: number;
  }>;
}

const STATUS_COLORS = {
  enabled: '#10b981',
  disabled: '#6b7280',
};

export default function Dashboard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/stats');
      setStats(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load dashboard statistics');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="text-white text-center">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-900/50 border border-red-700 rounded-lg p-4 text-red-200">
          {error}
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const successRate = stats.total_executions_24h > 0
    ? ((stats.successful_executions_24h / stats.total_executions_24h) * 100).toFixed(1)
    : '0';

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Dashboard</h1>
        <p className="text-slate-400">Overview of your cronjob system</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <Clock className="w-8 h-8 text-blue-500" />
            <span className="text-2xl font-bold text-white">{stats.total_jobs}</span>
          </div>
          <h3 className="text-slate-300 font-medium">Total Jobs</h3>
          <p className="text-xs text-slate-500 mt-1">
            {stats.enabled_jobs} enabled, {stats.disabled_jobs} disabled
          </p>
        </div>

        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <CheckCircle className="w-8 h-8 text-green-500" />
            <span className="text-2xl font-bold text-white">{stats.successful_executions_24h}</span>
          </div>
          <h3 className="text-slate-300 font-medium">Successful (24h)</h3>
          <p className="text-xs text-slate-500 mt-1">
            {successRate}% success rate
          </p>
        </div>

        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <XCircle className="w-8 h-8 text-red-500" />
            <span className="text-2xl font-bold text-white">{stats.failed_executions_24h}</span>
          </div>
          <h3 className="text-slate-300 font-medium">Failed (24h)</h3>
          <p className="text-xs text-slate-500 mt-1">
            {stats.recent_failures.length} recent failures
          </p>
        </div>

        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <Activity className="w-8 h-8 text-purple-500" />
            <span className="text-2xl font-bold text-white">{stats.avg_execution_time_ms}ms</span>
          </div>
          <h3 className="text-slate-300 font-medium">Avg Execution Time</h3>
          <p className="text-xs text-slate-500 mt-1">
            Last 24 hours
          </p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Executions by Hour */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Executions by Hour (Last 24h)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stats.executions_by_hour}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="hour" stroke="#94a3b8" />
              <YAxis stroke="#94a3b8" />
              <Tooltip
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                labelStyle={{ color: '#e2e8f0' }}
              />
              <Legend />
              <Bar dataKey="successful" fill="#10b981" name="Successful" />
              <Bar dataKey="failed" fill="#ef4444" name="Failed" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Jobs by Status */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Jobs by Status</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={stats.jobs_by_status}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ status, count, percent }) => `${status}: ${count} (${(percent * 100).toFixed(0)}%)`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="count"
              >
                {stats.jobs_by_status.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.status as keyof typeof STATUS_COLORS] || '#6b7280'} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Tables Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Next Scheduled Jobs */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-5 h-5 text-yellow-500" />
            <h3 className="text-lg font-semibold text-white">Next Scheduled Jobs</h3>
          </div>
          <div className="space-y-3">
            {stats.next_scheduled_jobs.length === 0 ? (
              <p className="text-slate-400 text-sm">No jobs scheduled</p>
            ) : (
              stats.next_scheduled_jobs.slice(0, 5).map((job) => (
                <div key={job.job_id} className="flex items-center justify-between p-3 bg-slate-900 rounded-lg">
                  <div>
                    <p className="text-white font-medium">{job.name}</p>
                    <p className="text-xs text-slate-400">ID: {job.job_id}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-blue-400">{new Date(job.next_run).toLocaleString()}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent Failures */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <h3 className="text-lg font-semibold text-white">Recent Failures</h3>
          </div>
          <div className="space-y-3">
            {stats.recent_failures.length === 0 ? (
              <p className="text-slate-400 text-sm">No recent failures</p>
            ) : (
              stats.recent_failures.slice(0, 5).map((failure) => (
                <div key={failure.id} className="p-3 bg-red-900/20 border border-red-700/50 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-white font-medium">{failure.job_name}</p>
                    <p className="text-xs text-slate-400">{new Date(failure.started_at).toLocaleString()}</p>
                  </div>
                  <p className="text-sm text-red-300 truncate">{failure.error_message}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
